import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { instanceOfDateString } from '../propTypes';
import { dateMax, dateMin } from 'javascripts/util/dateFns';
import { clampDate, willZoom, ZOOM_DIRECTION, zoomRoadmap } from '../helpers';
import { noop } from 'lodash';
import $ from 'jquery';
import styles from '../ScaleBar.module.css';
import {
  zoomToCustomRange,
  zoomToPresetRange,
  PRESET_DATES,
  ACTIVE_BUTTON_STATE,
  deriveInitialPresetState,
} from './utils';
import customElementBoolAttr from 'javascripts/util/customElementBoolAttr';

interface PresetNavigationBarProps {
  startDate: string;
  endDate: string;
  visibleStartDate: string;
  visibleEndDate: string;
  onSetVisibleDates: (startDate: string, endDate: string) => void;
  onSetEndDate: (date: string) => void;
  onSetStartDate: (date: string) => void;
  hideWeekends: boolean;
  timescale: string;
  zoom?: string;
  minStartDate: string;
  maxEndDate: string;
  onSetTimescale: (timescale: string) => void;
  onSetZoom: (zoom: 'presetRange' | 'fit' | 'custom') => void;
}

const PresetNavigationBar: React.FC<PresetNavigationBarProps> = props => {
  const {
    hideWeekends,
    timescale,
    zoom,
    onSetVisibleDates,
    maxEndDate,
    minStartDate,
    onSetEndDate,
    onSetStartDate,
    onSetTimescale,
    onSetZoom,
  } = props;

  const startDate = clampDate(props.startDate);
  const endDate = clampDate(props.endDate);
  const visibleStartDate = dateMax(startDate, props.visibleStartDate);
  const visibleEndDate = dateMin(endDate, props.visibleEndDate);

  // The state of the clicked button in the preset navigation bar
  const [buttonState, setButtonState] = useState(
    deriveInitialPresetState(
      visibleStartDate,
      visibleEndDate,
      minStartDate,
      maxEndDate,
      zoom
    )
  );

  // When the visible date is updated, do it on the next animation frame to make visuals smooth
  const onSetVisibleDatesHandler = useCallback(
    (visibleStartDate, visibleEndDate) => {
      requestAnimationFrame(() => {
        onSetVisibleDates(visibleStartDate, visibleEndDate);
      });
    },
    [onSetVisibleDates]
  );

  // Props needed to set custom date
  const customDateProps = {
    startDate,
    hideWeekends,
    maxEndDate,
    minStartDate,
    onSetEndDate,
    onSetStartDate,
    onSetVisibleDatesHandler,
    onSetZoom,
  };

  const [isCustomDatesShown, setIsCustomDatesShown] = useState(false);

  // Watch the <aha-menu> and when it is opened, set the `isCustomDatesShown` state to true/false to force the dateselector useEffect below to run.
  useEffect(() => {
    const menu = document.getElementById('preset-nav-bar__menu');
    document.addEventListener('aha-menu:open', e => {
      if (e.target === menu) setIsCustomDatesShown(true);
    });
    document.addEventListener('aha-menu:close', e => {
      if (e.target === menu) setIsCustomDatesShown(false);
    });
  }, []);

  // UseEffect Loads the Jquery custom date range selector.
  useEffect(() => {
    if (!isCustomDatesShown) return;

    // Generate the custom start date picker
    const startDatePicker = $('.qz-input-datepicker-start-date');
    (startDatePicker as any)
      .datepicker({
        // dateFormat rather than format
        format: 'yyyy-mm-dd', // Match Rails' default date format.
        language: '<%= I18n.locale %>',
        defaultViewDate: visibleStartDate,
        todayHighlight: true,
        autoclose: true,
        orientation: 'top',
      })
      .on('show', function () {
        const picker = $(this).data('datepicker').picker.get(0);
        if (picker.showPopover) {
          picker.popover = 'manual';
          picker.showPopover();
        }
      })
      .on('hide', function () {
        const picker = $(this).data('datepicker').picker.get(0);
        if (picker.hidePopover) {
          picker.hidePopover();
        }
      });

    // The 'selected' date does not update on its own correctly, so we force it
    startDatePicker.datepicker('update', visibleStartDate);

    // Call custom range set when someone changes the start date
    startDatePicker.on('changeDate', e => {
      const date = (e as any).target.value;
      setButtonState(ACTIVE_BUTTON_STATE.CUSTOM);
      zoomToCustomRange(date, visibleEndDate, customDateProps);
    });

    // Generate the custom end date picker
    const endDatePicker = $('.qz-input-datepicker-end-date');
    (endDatePicker as any)
      .datepicker({
        // dateFormat rather than format
        format: 'yyyy-mm-dd', // Match Rails' default date format.
        language: '<%= I18n.locale %>',
        defaultViewDate: visibleEndDate,
        todayHighlight: true,
        autoclose: true,
        orientation: 'top',
      })
      .on('show', function () {
        const picker = $(this).data('datepicker').picker.get(0);
        if (picker.showPopover) {
          picker.popover = 'manual';
          picker.showPopover();
        }
      })
      .on('hide', function () {
        const picker = $(this).data('datepicker').picker.get(0);
        if (picker.hidePopover) {
          picker.hidePopover();
        }
      });

    // The 'selected' date does not update on its own correctly, so we force it
    endDatePicker.datepicker('update', visibleEndDate);

    // Call custom range set when someone changes the end date
    endDatePicker.on('changeDate', e => {
      setButtonState(ACTIVE_BUTTON_STATE.CUSTOM);
      const date = (e as any).target.value;
      zoomToCustomRange(visibleStartDate, date, customDateProps);
    });

    // Run the cleanup function
    return () => {
      startDatePicker.off('changeDate show hide');
      endDatePicker.off('changeDate show hide');
      startDatePicker.datepicker('destroy');
      endDatePicker.datepicker('destroy');
    };
    // Since we are using jquery outside react flow, we don't want this
    // reconstructed for ever date change. So disable eslint here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCustomDatesShown, visibleEndDate, visibleStartDate]);

  // On click handler to Zoom to a given preset value
  const onZoomToPresetRangeHandler = (preset, buttonState) => {
    zoomToPresetRange(preset, {
      ...customDateProps,
      // Day count in the zoom presets include weekends, so don't hide them from the calculation
      hideWeekends: false,
    });
    onSetZoom('presetRange');
  };

  // On click handler to zoom to a given fit handler
  const onZoomToFitHandler = () => {
    // We want to also set the start and end date here so we don't have a lot of extra horiztonal scroll
    // This is important when importing to dashboards for panel views.
    onSetEndDate(maxEndDate);
    onSetStartDate(minStartDate);

    onSetZoom('fit');
    onSetVisibleDatesHandler(minStartDate, maxEndDate);
  };

  const scrollDistancePercent = 10;
  let canZoom = willZoom({
    visibleStartDate,
    visibleEndDate,
    startDate: minStartDate,
    endDate: maxEndDate,
    hideWeekends,
    scrollDistancePercent,
  });

  // On click handler to zoom IN or OUT per click
  const onZoomClickHandler = zoomDirection => {
    let { willChangeZoom } = zoomRoadmap({
      visibleStartDate,
      visibleEndDate,
      hideWeekends,
      startDate: minStartDate,
      endDate: maxEndDate,
      scrollDistancePercent,
      setVisibleDates: onSetVisibleDatesHandler,
      zoomDirection,
      anchorDate: null,
    });
    if (willChangeZoom) {
      setButtonState(ACTIVE_BUTTON_STATE.CUSTOM);
      onSetZoom('custom');
    }
  };

  // generate class names for preset buttons
  const buttonClassname = activeName =>
    classnames({
      active: activeName === setButtonState,
    });

  return (
    <div className={styles.presetNavigationBar}>
      <aha-radio-button-group
        size='mini'
        style={{ '--aha-radio-button--selected-transition-time': '0s' }}
      >
        <aha-radio-button
          selected={customElementBoolAttr(
            buttonState === ACTIVE_BUTTON_STATE.THREE_MONTHS
          )}
          onClick={() => {
            setButtonState(ACTIVE_BUTTON_STATE.THREE_MONTHS);
            onZoomToPresetRangeHandler(
              PRESET_DATES.THREE_MONTHS,
              ACTIVE_BUTTON_STATE.THREE_MONTHS
            );
          }}
        >
          3M
        </aha-radio-button>
        <aha-radio-button
          class={buttonClassname(ACTIVE_BUTTON_STATE.SIX_MONTHS)}
          selected={customElementBoolAttr(
            buttonState === ACTIVE_BUTTON_STATE.SIX_MONTHS
          )}
          onClick={() => {
            setButtonState(ACTIVE_BUTTON_STATE.SIX_MONTHS);
            onZoomToPresetRangeHandler(
              PRESET_DATES.SIX_MONTHS,
              ACTIVE_BUTTON_STATE.SIX_MONTHS
            );
          }}
        >
          6M
        </aha-radio-button>
        <aha-radio-button
          class={buttonClassname(ACTIVE_BUTTON_STATE.ONE_YEAR)}
          selected={customElementBoolAttr(
            buttonState === ACTIVE_BUTTON_STATE.ONE_YEAR
          )}
          onClick={() => {
            setButtonState(ACTIVE_BUTTON_STATE.ONE_YEAR);
            onZoomToPresetRangeHandler(
              PRESET_DATES.ONE_YEAR,
              ACTIVE_BUTTON_STATE.ONE_YEAR
            );
          }}
        >
          1Y
        </aha-radio-button>
        <aha-radio-button
          class={buttonClassname(ACTIVE_BUTTON_STATE.FIT)}
          selected={customElementBoolAttr(
            buttonState === ACTIVE_BUTTON_STATE.FIT
          )}
          onClick={() => {
            setButtonState(ACTIVE_BUTTON_STATE.FIT);
            onZoomToFitHandler();
          }}
        >
          Fit
        </aha-radio-button>

        <aha-menu id='preset-nav-bar__menu'>
          <aha-radio-button
            slot='control'
            aria-label='Select custom date range'
            selected={customElementBoolAttr(
              buttonState === ACTIVE_BUTTON_STATE.CUSTOM
            )}
            prevent-click // This button is not set to "selected" when clicked on, can only be set to selected programattically when the `activeButton` state changes.
          >
            Custom <i className='fa-solid fa-caret-down' />
          </aha-radio-button>
          <aha-menu-content>
            <div className={styles.customDatesMenu}>
              <div>
                <label
                  className='flex-row flex-row--align-center mr-5'
                  htmlFor='zoom_start_date'
                >
                  <span className='mr-3'>Start:</span>
                  <span className='input-append'>
                    <input
                      type='text'
                      name='zoom_start_date'
                      id='zoom_start_date'
                      aria-label='Set start date'
                      value={visibleStartDate}
                      onChange={noop} // Change handled by jquery datepicker
                      className={classnames(
                        styles.datePickerInput,
                        'input-small',
                        'qz-input-datepicker-start-date'
                      )}
                    />
                    <span className={classnames('add-on', styles.addOn)}>
                      <i className='fa-solid fa-calendar-days' />
                    </span>
                  </span>
                </label>
              </div>
              <div>
                <label
                  className='flex-row flex-row--align-center'
                  htmlFor='zoom_end_date'
                >
                  <span className='mr-3'>End:</span>
                  <span className='input-append'>
                    <input
                      type='text'
                      name='zoom_end_date'
                      id='zoom_end_date'
                      aria-label='Set end date'
                      value={visibleEndDate}
                      onChange={noop} // Change handled by jquery datepicker
                      className={classnames(
                        styles.datePickerInput,
                        'input-small',
                        'qz-input-datepicker-end-date'
                      )}
                    />
                    <span className={classnames('add-on', styles.addOn)}>
                      <i className='fa-solid fa-calendar-days' />
                    </span>
                  </span>
                </label>
              </div>
            </div>
            <div className={styles.calendarResolution}>
              <label>
                Calendar resolution:
                <select
                  className={styles.calendarResolutionSelect}
                  value={timescale || 'auto'}
                  onChange={e => onSetTimescale(e.target.value)}
                  aria-label='Calendar resolution'
                >
                  <option value='auto'>Set automatically</option>
                  <option value='quarters'>Quarters</option>
                  <option value='months'>Months</option>
                  <option value='weeks'>Weeks</option>
                  <option value='days'>Days</option>
                </select>
              </label>
            </div>
          </aha-menu-content>
        </aha-menu>
      </aha-radio-button-group>
      <div className='btn-group pl-2'>
        <button
          className='btn btn-mini'
          aria-label='Zoom In'
          onClick={() => onZoomClickHandler(ZOOM_DIRECTION.IN)}
          disabled={!canZoom[ZOOM_DIRECTION.IN]}
        >
          <i className='fa-regular fa-magnifying-glass-plus' />
        </button>
        <button
          className='btn btn-mini'
          aria-label='Zoom Out'
          onClick={() => onZoomClickHandler(ZOOM_DIRECTION.OUT)}
          disabled={!canZoom[ZOOM_DIRECTION.OUT]}
        >
          <i className='fa-regular fa-magnifying-glass-minus' />
        </button>
      </div>
    </div>
  );
};

PresetNavigationBar.propTypes = {
  startDate: instanceOfDateString.isRequired, // data's minimum start date
  endDate: instanceOfDateString.isRequired, // data's maximum end date
  visibleStartDate: instanceOfDateString.isRequired, // current timescale's min date
  visibleEndDate: instanceOfDateString.isRequired, // current timescale's max date
  onSetVisibleDates: PropTypes.func.isRequired,
  onSetEndDate: PropTypes.func.isRequired,
  onSetStartDate: PropTypes.func.isRequired,
  hideWeekends: PropTypes.bool.isRequired,
  timescale: PropTypes.string, // resolution of tic marks on the time axis
  zoom: PropTypes.string, // threeMonth, sixMonth, oneYear, fit, custom, as defined in ACTIVE_BUTTON_STATE
  minStartDate: instanceOfDateString.isRequired, // earliest date we'd want to set the calendar start date to.
  maxEndDate: instanceOfDateString.isRequired, // latest date we'd want to set the calendar end date to. Changes for 3M,6M,1Y,fit intervals
  onSetTimescale: PropTypes.func.isRequired,
  onSetZoom: PropTypes.func.isRequired,
};

const memoPresetNavigationBar = React.memo(PresetNavigationBar);

export default memoPresetNavigationBar;
